import { z } from 'zod';
// ============================================================================
// BASE SCHEMAS
// ============================================================================
export const CharacterGenderSchema = z.enum([
    'male',
    'female',
    'gender_neutral',
]);
// Core character properties that are shared across all contexts
export const CharacterCoreSchema = z.object({
    name: z.string().min(1, 'Character name is required'),
    gender: CharacterGenderSchema,
    age: z.number().int().min(0).max(120),
});
// Extended character properties for database storage
export const CharacterExtendedSchema = CharacterCoreSchema.extend({
    character_traits: z.array(z.string()),
    character_type: z.string(),
    appearance: z.string().nullable(),
});
// ============================================================================
// DATABASE SCHEMAS
// ============================================================================
// Complete database character schema with auto-generated fields
export const CharacterDBSchema = CharacterExtendedSchema.extend({
    id: z.uuid(),
    user_id: z.uuid(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
// ============================================================================
// FORM SCHEMAS
// ============================================================================
// Character form schema for file uploads (allows optional age/gender during filling)
export const CharacterFormSchema = z.object({
    name: z.string().min(1, 'Character name is required'),
    gender: CharacterGenderSchema.optional(),
    age: z.number().int().min(0).max(120).optional(),
    reference_image: z
        .any()
        .refine((file) => file !== null && file !== undefined, 'Reference image is required'),
});
// Character selection schema for story creation
export const CharacterSelectionSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('existing'),
        existingCharacterId: z.string().uuid('Must select an existing character'),
        newCharacter: z.undefined(),
    }),
    z.object({
        type: z.literal('new'),
        existingCharacterId: z.undefined(),
        newCharacter: CharacterFormSchema,
    }),
]);
// Form with multiple character selections
export const CharacterSelectionArraySchema = z.object({
    characterSelections: z.array(CharacterSelectionSchema),
});
// Form with multiple characters (legacy for backward compatibility)
export const CharacterFormArraySchema = z.object({
    characters: z.array(CharacterFormSchema),
});
// ============================================================================
// API REQUEST SCHEMAS
// ============================================================================
// Base64 image validation
export const Base64ImageDataSchema = z
    .string()
    .regex(/^data:image\/(jpeg|jpg|png|gif|webp);base64,/, 'Must be a valid base64 image data URL')
    .min(100, 'Image data too small')
    .max(20 * 1024 * 1024, 'Image data too large (max 20MB)');
// Character with image data for API requests
export const CharacterRequestSchema = CharacterCoreSchema.extend({
    referenceImageData: Base64ImageDataSchema,
});
// Create multiple characters request
export const CreateCharactersRequestSchema = z.object({
    characters: z
        .array(CharacterRequestSchema)
        .min(1, 'At least one character is required')
        .max(10, 'Maximum 10 characters allowed'),
    styleTemplateId: z.uuid(),
});
// Character attributes inference request
export const InferCharacterAttributesRequestSchema = z.object({
    name: z.string().min(1, 'Character name is required'),
    age: z.number().min(0, 'Age must be a positive number'),
    gender: z.string().min(1, 'Gender is required'),
    imageData: Base64ImageDataSchema,
});
// Character sheet generation request (uses existing character data)
export const GenerateCharacterSheetRequestSchema = z.object({
    character: CharacterExtendedSchema.extend({
        id: z.uuid(),
    }),
});
// Character preview image generation request (uses existing character data and style template)
export const GenerateCharacterPreviewImageRequestSchema = z.object({
    character: CharacterExtendedSchema.extend({
        id: z.uuid(),
    }),
    styleTemplate: z.object({
        id: z.string(),
        title: z.string(),
        description: z.string(),
    }),
});
// ============================================================================
// API RESPONSE SCHEMAS
// ============================================================================
// Character attributes inference response
export const InferCharacterAttributesResponseSchema = z.object({
    appearance: z.string().nullable(),
});
// Character sheet generation response
export const GenerateCharacterSheetResponseSchema = z.object({
    base64Data: z.string(),
    mimeType: z
        .string()
        .regex(/^image\/(jpeg|jpg|png|gif|webp)$/, 'Must be a valid image MIME type'),
    message: z.string(),
});
// Character preview image generation response
export const GenerateCharacterPreviewImageResponseSchema = z.object({
    base64Data: z.string(),
    mimeType: z
        .string()
        .regex(/^image\/(jpeg|jpg|png|gif|webp)$/, 'Must be a valid image MIME type'),
    message: z.string(),
});
// Character with URLs for API responses
export const CharacterWithUrlsSchema = CharacterExtendedSchema.extend({
    id: z.uuid(),
    referenceImageUrl: z.string().nullable(),
    previewImageUrl: z.string().nullable(),
});
// Success response for character creation
export const CreateCharactersSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        characters: z.array(CharacterWithUrlsSchema),
    }),
});
// ============================================================================
// SOFT DELETE SCHEMAS
// ============================================================================
// Soft delete character request schema
export const SoftDeleteCharacterRequestSchema = z.object({
    id: z.uuid(),
});
// Soft delete character success response schema
export const SoftDeleteCharacterSuccessResponseSchema = z.object({
    type: z.literal('success'),
    data: z.object({
        message: z.string(),
        deletedCharacterId: z.string(),
    }),
});
// Soft delete character response schema (union type)
export const SoftDeleteCharacterResponseSchema = z.discriminatedUnion('type', [
    SoftDeleteCharacterSuccessResponseSchema,
    ErrorResponseSchema,
]);
// ============================================================================
// ERROR HANDLING SCHEMAS
// ============================================================================
// Import unified error schemas
import { ErrorResponseSchema } from './errors';
// Union response type
export const CreateCharactersResponseSchema = z.discriminatedUnion('type', [
    CreateCharactersSuccessResponseSchema,
    ErrorResponseSchema,
]);
